import { PADDLE_HEIGHT, paddleSegmentAt, projectedBallY, type GameState } from "./game.ts";
import { type CombatState, type PlayerSide, type RocketState } from "./protocol.ts";

export const FIRE_COOLDOWN_SECONDS = 0.35;
export const MAX_ROCKETS_PER_PLAYER = 4;

function crosses(start: number, end: number, target: number): boolean {
  return start <= target && end >= target || start >= target && end <= target;
}

export function createRocket(state: GameState, side: PlayerSide, id: number): RocketState {
  const left = side === "left";
  const paddleY = left ? state.playerY : state.opponentY;
  return {
    id,
    owner: side,
    x: left ? 4 : state.width - 5,
    y: paddleY + (PADDLE_HEIGHT - 1) / 2,
    vx: Math.abs(state.ballVx) * 2 * (left ? 1 : -1),
  };
}

export function ballShieldCollision(
  state: GameState,
  combat: CombatState,
  seconds: number,
): PlayerSide | null {
  const nextX = state.ballX + state.ballVx * seconds;
  const contacts: Array<{ side: PlayerSide; ratio: number }> = [];
  for (const side of ["left", "right"] as const) {
    if (!combat.shields[side]) continue;
    const shieldX = side === "left" ? 6 : state.width - 7;
    if (!crosses(state.ballX, nextX, shieldX)) continue;
    const ratio = nextX === state.ballX ? 0 : (shieldX - state.ballX) / (nextX - state.ballX);
    const collisionY = projectedBallY(state, seconds * ratio);
    const paddleY = side === "left" ? state.playerY : state.opponentY;
    if (paddleSegmentAt(collisionY, paddleY) !== null) contacts.push({ side, ratio });
  }
  contacts.sort((a, b) => a.ratio - b.ratio);
  return contacts[0]?.side ?? null;
}

export function advanceRockets(
  combat: CombatState,
  state: GameState,
  seconds: number,
): CombatState {
  const next: CombatState = {
    rockets: [],
    shields: { ...combat.shields },
    damage: { left: [...combat.damage.left], right: [...combat.damage.right] },
  };
  for (const rocket of combat.rockets) {
    const nextX = rocket.x + rocket.vx * seconds;
    const target: PlayerSide = rocket.owner === "left" ? "right" : "left";
    const paddleY = target === "left" ? state.playerY : state.opponentY;
    const segment = paddleSegmentAt(rocket.y, paddleY);
    const shieldX = target === "left" ? 6 : state.width - 7;
    if (combat.shields[target] && segment !== null && crosses(rocket.x, nextX, shieldX)) continue;
    const paddleX = target === "left" ? 3 : state.width - 4;
    if (
      segment !== null
      && !next.damage[target].includes(segment)
      && crosses(rocket.x, nextX, paddleX)
    ) {
      next.damage[target].push(segment);
      next.damage[target].sort((a, b) => a - b);
      continue;
    }
    if (nextX >= 0 && nextX < state.width) next.rockets.push({ ...rocket, x: nextX });
  }
  return next;
}
